// Sidebar com menu animado e ícones
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  FileSearch,
  BarChart3,
  Settings,
  X,
  HelpCircle,
} from 'lucide-react';
import { cn } from '../../utils/cn';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  children?: { label: string; path: string }[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/app',
  },
  {
    label: 'Emissão',
    icon: FileText,
    path: '/app/emissao',
    children: [
      { label: 'Nota Fiscal Eletrônica (NFe)', path: '/app/emissao/nfe' },
      { label: 'Nota Fiscal de Serviços (NFSe)', path: '/app/emissao/nfse' },
      { label: 'Nota Fiscal de Consumidor (NFCe)', path: '/app/emissao/nfce' },
    ],
  },
  {
    label: 'Cadastros',
    icon: Users,
    path: '/app/cadastros',
    children: [
      { label: 'Clientes', path: '/app/cadastros/clientes' },
      { label: 'Fornecedores', path: '/app/cadastros/fornecedores' },
      { label: 'Produtos', path: '/app/cadastros/produtos' },
      { label: 'Serviços', path: '/app/cadastros/servicos' },
      { label: 'Empresa', path: '/app/cadastros/empresa' },
      { label: 'Transportadora', path: '/app/cadastros/transportadora' },
    ],
  },
  {
    label: 'Consultas',
    icon: FileSearch,
    path: '/app/consultas',
  },
  {
    label: 'Relatórios',
    icon: BarChart3,
    path: '/app/relatorios',
  },
  {
    label: 'Configurações',
    icon: Settings,
    path: '/app/configuracoes',
  },
  {
    label: 'Suporte',
    icon: HelpCircle,
    path: '/app/suporte',
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay Mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[45] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-[50] h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800',
          'transform transition-transform duration-300 ease-in-out shadow-sm',
          'lg:translate-x-0 lg:fixed',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo e Nome - Topo da Sidebar */}
          <div className="h-16 px-4 border-b border-gray-200 dark:border-gray-800 flex items-center bg-white dark:bg-gray-900">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-white font-bold text-xs">NFe</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">proNotas</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Sistema de Gestão</span>
              </div>
            </div>
          </div>

          {/* Header Mobile */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 lg:hidden bg-white dark:bg-gray-900">
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Menu
            </span>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <ul className="space-y-1">
              {menuItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ease-out',
                      isActive(item.path)
                        ? 'bg-primary-100/30 dark:bg-primary-900/15 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/60 dark:hover:bg-gray-800/50'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>

                  {/* Submenu */}
                  {item.children && isActive(item.path) && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={onClose}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ease-out text-sm',
                              location.pathname === child.path
                                ? 'bg-primary-100/30 dark:bg-primary-900/15 text-primary-700 dark:text-primary-300'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100/60 dark:hover:bg-gray-800/50'
                            )}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 dark:bg-primary-400"></span>
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
}
